import { captureException } from '@sentry/browser';
import { authClient, AuthUserCompanyRole } from './auth-client.ts';

export interface Auth {
  id: string;
  username: string;
  email: string;
  role: string[];
  userCompanyRole: AuthUserCompanyRole[];

  accessTokenExpiresAt: Date;
  accessToken: string;
  refreshToken: string;
}

// Refresh 10 seconds before expiry
const TOKEN_BUFFER_MS = 10_000;
const AUTH_KEY = 'auth';

class TokenManager {
  private listeners = new Set<() => void>();
  private inflightRefresh: Promise<string | null> | null = null;
  private snapshotCache: { raw: string; parsed: Auth } | null = null;

  // Arrow functions ensure stable `this` when passed directly to useSyncExternalStore
  getSnapshot = (): Auth | null => {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) {
      this.snapshotCache = null;
      return null;
    }
    if (this.snapshotCache?.raw === raw) {
      return this.snapshotCache.parsed;
    }
    try {
      const parsed = JSON.parse(raw) as Auth;
      this.snapshotCache = { raw, parsed };
      return parsed;
    } catch {
      this.snapshotCache = null;
      return null;
    }
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private notify(): void {
    this.listeners.forEach((l) => { l(); });
  }

  saveAuth = (auth: Auth): void => {
    localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
    this.snapshotCache = null;
    this.notify();
  };

  clearAuth = (): void => {
    localStorage.removeItem(AUTH_KEY);
    this.snapshotCache = null;
    this.notify();
  };

  getValidAccessToken = async (retry = 3): Promise<string | null> => {
    // Deduplicate concurrent refresh calls - return existing inflight promise
    if (this.inflightRefresh) {
      return this.inflightRefresh;
    }

    const auth = this.getSnapshot();
    if (!auth) return null;

    const now = Date.now();
    const expiresAt = new Date(auth.accessTokenExpiresAt).getTime();

    // Token is still valid (with buffer)
    if (now < expiresAt - TOKEN_BUFFER_MS) {
      return auth.accessToken;
    }

    // Token expired or about to expire - refresh with deduplication
    this.inflightRefresh = this.doRefresh(auth, retry).finally(() => {
      this.inflightRefresh = null;
    });

    return this.inflightRefresh;
  };

  private async doRefresh(auth: Auth, retry: number): Promise<string | null> {
    try {
      const result = await authClient.refreshToken(auth.refreshToken);

      if (result === null) {
        // Refresh token invalid - clear auth and notify React
        this.clearAuth();
        return null;
      }

      const now = Date.now();
      this.saveAuth({
        ...auth,
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        accessTokenExpiresAt: new Date(now + result.expiresIn * 1000),
      });
      return result.accessToken;
    } catch (error) {
      captureException(error);

      if (retry > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, 1000));
        return this.doRefresh(auth, retry - 1);
      }

      return null;
    }
  }

  private async refreshIfNeeded(): Promise<void> {
    const auth = this.getSnapshot();
    if (!auth) return;

    const now = Date.now();
    const expiresAt = new Date(auth.accessTokenExpiresAt).getTime();

    if (now >= expiresAt - TOKEN_BUFFER_MS) {
      await this.getValidAccessToken();
    }
  }

  init(): void {
    // Refresh when app returns to foreground - handles mobile background kills
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.refreshIfNeeded();
      }
    });

    // Sync auth state across browser tabs
    window.addEventListener('storage', (event) => {
      if (event.key === AUTH_KEY || event.key === null) {
        this.snapshotCache = null;
        this.notify();
      }
    });
  }
}

export const tokenManager = new TokenManager();
tokenManager.init();
